import { useEffect, useMemo, useState } from 'react'
import type { Locale } from '@chess-uno/shared-protocol'
import rulesDe from '../../../../docs/RULES.de.md?raw'
import rulesEn from '../../../../docs/RULES.en.md?raw'
import rulesEs from '../../../../docs/RULES.es.md?raw'
import rulesHi from '../../../../docs/RULES.hi.md?raw'
import rulesFr from '../../../../docs/RULES.md?raw'
import type { StoredSession } from '../api/session.js'
import { SiteFooter, TopBar } from '../components/Chrome.js'
import { CampaignNote } from '../components/CampaignNote.js'
import { useI18n } from '../i18n/index.js'

/**
 * Rendering of the rules (docs/RULES.md and its translations docs/RULES.<locale>.md
 * stay the single sources): numbered headings with anchors, lists, tables,
 * bold and code. The effects table gets a card chip on each row.
 */

function escape(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;')
}

/** "[expérimental]" / "[experimental]" tag, in every language. */
const EXPERIMENTAL = /\*\*\[(expérimental|experimental|experimentell|प्रायोगिक)\]\*\*/g

function inline(text: string): string {
  return escape(text)
    .replace(EXPERIMENTAL, '<span class="exp-tag">$1</span>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
}

/** Anchor of a heading: accents dropped, letters of any script kept (Devanagari included). */
function slug(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{M}\p{N}]+/gu, '-')
    .replace(/^-|-$/g, '')
}

/** Visual chip of the cards in the effects table. */
const CARD_CHIPS: Record<string, { cls: string; label: string }> = {
  'Chiffre 1 à 9': { cls: 'chip-number', label: '7' },
  'Number 1 to 9': { cls: 'chip-number', label: '7' },
  'Número 1 a 9': { cls: 'chip-number', label: '7' },
  'Zahl 1 bis 9': { cls: 'chip-number', label: '7' },
  'संख्या 1 से 9': { cls: 'chip-number', label: '7' },
  '0': { cls: 'chip-zero', label: '0' },
  'Passe ton tour': { cls: 'chip-skip', label: '↷' },
  Skip: { cls: 'chip-skip', label: '↷' },
  'Pierde turno': { cls: 'chip-skip', label: '↷' },
  Aussetzen: { cls: 'chip-skip', label: '↷' },
  स्किप: { cls: 'chip-skip', label: '↷' },
  '+2': { cls: 'chip-plus2', label: '+2' },
  '+4': { cls: 'chip-joker', label: '+4' },
  Inversion: { cls: 'chip-reverse', label: '↻' },
  Reverse: { cls: 'chip-reverse', label: '↻' },
  Inversión: { cls: 'chip-reverse', label: '↻' },
  Richtungswechsel: { cls: 'chip-reverse', label: '↻' },
  रिवर्स: { cls: 'chip-reverse', label: '↻' },
  Joker: { cls: 'chip-joker', label: '♛' },
  जोकर: { cls: 'chip-joker', label: '♛' },
}

/** Header of the first column of the card effects table, in every language. */
const CARD_HEADERS = new Set(['Carte', 'Card', 'Carta', 'Karte', 'कार्ड'])

interface Section {
  id: string
  num: string
  /** Title without markup (table of contents). */
  title: string
  /** Original Markdown title (body). */
  rich: string
}

function render(md: string): { title: string; intro: string; sections: Section[]; html: string } {
  const lines = md.split('\n')
  const out: string[] = []
  const sections: Section[] = []
  let title = ''
  let intro = ''
  let list: string[] = []
  let ordered = false
  let table: string[][] = []

  const flush = () => {
    if (list.length) {
      const tag = ordered ? 'ol' : 'ul'
      out.push(`<${tag}>${list.map((l) => `<li>${inline(l)}</li>`).join('')}</${tag}>`)
      list = []
    }
    if (table.length) {
      const [head, ...rows] = table
      const isCards = CARD_HEADERS.has(head![0]!)
      const cell = (c: string, i: number) => {
        const chip = isCards && i === 0 ? CARD_CHIPS[c] : undefined
        const content = chip ? `<span class="card-chip ${chip.cls}">${escape(chip.label)}</span>${inline(c)}` : inline(c)
        return `<td>${content}</td>`
      }
      out.push(
        `<div class="table-wrap"><table${isCards ? ' class="cards-table"' : ''}><thead><tr>${head!
          .map((c) => `<th>${inline(c)}</th>`)
          .join('')}</tr></thead><tbody>${rows.map((r) => `<tr>${r.map(cell).join('')}</tr>`).join('')}</tbody></table></div>`,
      )
      table = []
    }
  }

  for (const raw of lines) {
    const line = raw.trimEnd()
    if (line.startsWith('|')) {
      const cells = line.split('|').slice(1, -1).map((c) => c.trim())
      if (cells.every((c) => /^-+$/.test(c))) continue
      table.push(cells)
      continue
    }
    const bullet = /^\s*[-*] /.test(line)
    const numbered = /^\s*\d+\. /.test(line)
    if (bullet || numbered) {
      if (table.length || (list.length && ordered !== numbered)) flush()
      ordered = numbered
      list.push(line.replace(/^\s*([-*]|\d+\.) /, ''))
      continue
    }
    flush()
    if (line.startsWith('# ')) {
      title = line.slice(2)
    } else if (line.startsWith('## ')) {
      const text = line.slice(3)
      const m = /^(\d+)\.\s*(.*)$/.exec(text)
      const full = m ? m[2]! : text
      // The table of contents shows the title without markup (nor the "experimental" tag).
      const plain = full.replace(EXPERIMENTAL, '').replace(/\*\*/g, '').trim()
      const section = { id: slug(plain), num: m ? m[1]! : '', title: plain, rich: full }
      sections.push(section)
      out.push(
        `<h2 id="${section.id}">${section.num ? `<span class="sec-num">${section.num}</span>` : ''}<span>${inline(section.rich)}</span></h2>`,
      )
    } else if (line.startsWith('### ')) out.push(`<h3>${inline(line.slice(4))}</h3>`)
    else if (line.trim() === '') continue
    else if (!intro && sections.length === 0) intro = inline(line)
    else out.push(`<p>${inline(line)}</p>`)
  }
  flush()
  return { title, intro, sections, html: out.join('\n') }
}

const DOCS: Record<Locale, ReturnType<typeof render>> = {
  fr: render(rulesFr),
  en: render(rulesEn),
  es: render(rulesEs),
  de: render(rulesDe),
  hi: render(rulesHi),
}

/** Visible section (highlighted in the table of contents). */
function useActiveSection(ids: string[]): string | null {
  const [active, setActive] = useState<string | null>(ids[0] ?? null)
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) setActive(visible[0].target.id)
      },
      { rootMargin: '-80px 0px -65% 0px' },
    )
    for (const id of ids) {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    }
    return () => observer.disconnect()
  }, [ids])
  return active
}

export function RulesPage({ session }: { session: StoredSession | null }) {
  const { locale, t } = useI18n()
  const doc = DOCS[locale]
  const ids = useMemo(() => doc.sections.map((s) => s.id), [doc])
  const active = useActiveSection(ids)
  return (
    <div className="app-shell">
      <TopBar session={session} />
      <CampaignNote />
      <main className="rules-page">
        <header className="page-head rules-head">
          <p className="eyebrow">{t.rules.eyebrow}</p>
          <h1>{doc.title.replace(/\s*\(.*\)\s*$/, '')}</h1>
          {doc.intro && <p className="lead" dangerouslySetInnerHTML={{ __html: doc.intro }} />}
        </header>
        <div className="rules-layout">
          <nav className="rules-toc" aria-label={t.rules.toc}>
            <p className="eyebrow">{t.rules.toc}</p>
            <ol>
              {doc.sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className={active === s.id ? 'active' : ''}>
                    <span className="sec-num">{s.num}</span>
                    {s.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
          <article className="rules" dangerouslySetInnerHTML={{ __html: doc.html }} data-testid="rules" />
        </div>
      </main>
      <SiteFooter />
    </div>
  )
}
